import { API_BASE } from '../Constants';
import { ChatFileData } from '../interfaces';

/**
 * Fetch file metadata and content from the chat_files API.
 */
export async function fetchChatFile(
  fileId: string,
  token: string,
): Promise<ChatFileData> {
  const response = await fetch(`${API_BASE}/api/chat_files/${fileId}`, {
    method: 'GET',
    headers: {
      Authorization: `Auth ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch chat file: ${response.status}`);
  }

  return response.json();
}

/**
 * Save user edits to an editable file.
 */
export async function saveChatFileContent(
  fileId: string,
  content: Record<string, any>,
  token: string,
): Promise<{ success: boolean; updated_at: string }> {
  const response = await fetch(`${API_BASE}/api/chat_files/${fileId}/content`, {
    method: 'PUT',
    headers: {
      Authorization: `Auth ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content }),
  });

  if (!response.ok) {
    throw new Error(`Failed to save file content: ${response.status}`);
  }

  return response.json();
}

/**
 * Export an editable file to binary format (XLSX, CSV, DOCX, TXT).
 * Returns the binary content as a Blob.
 */
export async function exportChatFile(
  fileId: string,
  format: string,
  token: string,
): Promise<Blob> {
  const response = await fetch(`${API_BASE}/api/chat_files/${fileId}/export`, {
    method: 'POST',
    headers: {
      Authorization: `Auth ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ format }),
  });

  if (!response.ok) {
    throw new Error(`Failed to export file: ${response.status}`);
  }

  return response.blob();
}

/**
 * Get the blob URL for streaming binary files (PDF, images).
 * Returns the API URL that can be used directly in img/iframe src attributes.
 */
export function getChatFileBlobUrl(fileId: string): string {
  return `${API_BASE}/api/chat_files/${fileId}/blob`;
}

/**
 * Download binary content for a file from GCS via the backend.
 */
export async function downloadChatFileBlob(
  fileId: string,
  token: string,
): Promise<Blob> {
  const response = await fetch(`${API_BASE}/api/chat_files/${fileId}/blob`, {
    method: 'GET',
    headers: {
      Authorization: `Auth ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to download file blob: ${response.status}`);
  }

  return response.blob();
}
